import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { AppShell } from '@/app/shell/app-shell';
import { PageSkeleton } from '@/components/feedback/skeleton';
import { PageErrorBoundary } from '@/components/feedback/error-boundary';
import { EmptyState } from '@/components/feedback/empty-state';
import { Button } from '@/components/primitives/button';
import { DEFAULT_ROUTE } from './routes';

// 頁面全部 lazy load，讓首屏只載入殼與今日桌面。
const TodayPage = lazy(() =>
  import('@/features/today/today-page').then((m) => ({ default: m.TodayPage })),
);
const TaskListPage = lazy(() =>
  import('@/features/tasks/task-list-page').then((m) => ({ default: m.TaskListPage })),
);
const BoardPage = lazy(() =>
  import('@/features/board/board-page').then((m) => ({ default: m.BoardPage })),
);
const CalendarPage = lazy(() =>
  import('@/features/calendar/calendar-page').then((m) => ({ default: m.CalendarPage })),
);
const NotesPage = lazy(() =>
  import('@/features/notes/notes-page').then((m) => ({ default: m.NotesPage })),
);
const ContactsPage = lazy(() =>
  import('@/features/contacts/contacts-page').then((m) => ({ default: m.ContactsPage })),
);
const RemindersPage = lazy(() =>
  import('@/features/reminders/reminders-page').then((m) => ({ default: m.RemindersPage })),
);
const BackupPage = lazy(() =>
  import('@/features/backup/backup-page').then((m) => ({ default: m.BackupPage })),
);
const TrashPage = lazy(() =>
  import('@/features/trash/trash-page').then((m) => ({ default: m.TrashPage })),
);
const SettingsPage = lazy(() =>
  import('@/features/settings/settings-page').then((m) => ({ default: m.SettingsPage })),
);

function Page({ children }: { children: React.ReactNode }) {
  return (
    <PageErrorBoundary>
      <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
    </PageErrorBoundary>
  );
}

function NotFoundPage() {
  return (
    <EmptyState
      title="找不到這個頁面"
      description="這個網址沒有對應的頁面。可能是連結過期，或是網址少了一段。"
      slothState="stuck"
      action={
        <Button variant="secondary" size="sm" onClick={() => window.history.back()}>
          回到上一頁
        </Button>
      }
    />
  );
}

export function AppRouter() {
  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<Navigate to={DEFAULT_ROUTE} replace />} />
        <Route
          path="/today"
          element={
            <Page>
              <TodayPage />
            </Page>
          }
        />
        <Route
          path="/tasks"
          element={
            <Page>
              <TaskListPage />
            </Page>
          }
        />
        <Route
          path="/board"
          element={
            <Page>
              <BoardPage />
            </Page>
          }
        />
        <Route
          path="/calendar"
          element={
            <Page>
              <CalendarPage />
            </Page>
          }
        />
        <Route
          path="/notes"
          element={
            <Page>
              <NotesPage />
            </Page>
          }
        />
        <Route
          path="/contacts"
          element={
            <Page>
              <ContactsPage />
            </Page>
          }
        />
        <Route
          path="/reminders"
          element={
            <Page>
              <RemindersPage />
            </Page>
          }
        />
        <Route
          path="/backup"
          element={
            <Page>
              <BackupPage />
            </Page>
          }
        />
        <Route
          path="/trash"
          element={
            <Page>
              <TrashPage />
            </Page>
          }
        />
        <Route
          path="/settings"
          element={
            <Page>
              <SettingsPage />
            </Page>
          }
        />
        <Route
          path="*"
          element={
            <Page>
              <NotFoundPage />
            </Page>
          }
        />
      </Route>
    </Routes>
  );
}
